// Common types for the ISE application

import type { NarrativeStaleness } from '../types/narrativeRefresh'

export interface User {
  username: string
  email?: string
  id?: number
}

export interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

export interface Tab {
  id: string
  type: 'quick' | 'deep' | 'comprehensive' | 'graph' | 'strategy-map' | 'initiative'
  title: string
  conversationId?: string
  workflowId?: string
  data?: any
  active: boolean
  closable: boolean
}

export interface TabsState {
  tabs: Tab[]
  activeTabId: string | null
}

export interface GraphNode {
  id: string
  label: string
  label_slug: string
  meta_id: string
  meta_name: string
  meta_description: string
  shape_type: string
  width: number
  height: number
  font_size: number
  x: number
  y: number
}

export interface GraphEdge {
  id: string
  source: string
  target: string
  label?: string
  relationship_type: string
}

export interface GraphData {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export interface GraphState {
  data: GraphData | null
  filters: {
    taxonomyLayers: string[]
    searchQuery: string
  }
  selectedNodeId: string | null
  loading: boolean
  error: string | null
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export interface ChatState {
  messages: ChatMessage[]
  pendingQuery: string
  loading: boolean
}

export interface Initiative {
  id: string
  initiative_id: string
  name: string
  primary_objective: string
  validation_status: string
  mediation_path: string
  assignee?: string
  status?: 'not_started' | 'in_progress' | 'completed'
  due_date?: string
  description?: string
  linked_node_ids?: string[]
}

export interface InitiativeState {
  initiatives: Initiative[]
  filters: {
    status: string[]
    assignee: string[]
  }
  selectedInitiativeId: string | null
  loading: boolean
  error: string | null
}

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

// OMAP response modes
// Legacy: 'quick' | 'deep' | 'comprehensive'
// New (Jan 2026): 'quick' | 'strategy' with planning paths
export type ResponseMode = 'quick' | 'strategy'
export type PlanningPath = 'express' | 'guided' | 'explore'

// Strategy Planning Mode types (Jan 2026)
export interface StrategyPlanningRequest {
  user_query: string
  company_id: number
  conversation_id: number
  user_id?: number
  planning_path: PlanningPath
}

export interface StrategyPlanningResponse {
  status: string
  error?: string
  conversation_id: number
  current_gate: 1 | 2 | 3 | 'completed'
  planning_path: PlanningPath
  ddca_result?: any
  query_intent?: string
  coverage_report?: CoverageReport
  mece_table?: MECETable
  strategy_map?: StrategyMap
  graph_context?: GraphContext
  progress?: ProgressUpdate
  approval_prompt?: ApprovalPrompt
  strategy_plan_document?: string  // Full markdown Strategy Plan document
  coverage_attestation?: string    // Coverage attestation from backend
  strategic_synthesis?: any        // Strategic synthesis from BSRE analysis
  strategic_objectives?: any[]     // Strategic objectives from analysis
}

export interface ProgressUpdate {
  phase: string
  confidence: number
  nodes_explored: number
  questions_completed: number
  gaps_identified: string[]
  current_pillar?: string
  reasoning_step?: string
  discovery_insight?: string
}

export interface ApprovalOption {
  key: string
  label: string
  description?: string
  shortcut?: string
}

export interface ApprovalPrompt {
  title: string
  options: ApprovalOption[]
  allow_custom_input: boolean
  gate: number
}

export interface StrategyAdvanceRequest {
  conversation_id: number
  user_id?: number
  decision_payload: {
    approved: boolean
    feedback?: string
    selected_option?: string
    modifications?: any
  }
}

export interface SyncReportRequest {
  conversation_id: number
  pending_edits: EditRecord[]
  current_document: string
  workspace_state: any
}

export interface EditRecord {
  tab: 'mece' | 'strategy_map' | 'factors'
  path: string
  old_value: any
  new_value: any
  timestamp: number
}

export interface ImpactAnalysisRequest {
  conversation_id: number
  edit_description: string
  old_value: any
  new_value: any
  pillar_name?: string
  mece_summary?: any
}

export interface ImpactAnalysisResponse {
  impact_analysis: string
  affected_elements: string[]
}

export interface StrategySession {
  session_id: string
  company_id: number
  user_id: number
  session_name: string
  workflow_state: any
  last_active: string
  status: 'active' | 'paused' | 'completed'
}

export interface QuickModeResponse {
  response: string
  chat_summary?: string | null
  conversation_id: string
  citations?: string[]
  graph_context?: GraphContext
}

// Deep Mode Plan Generation Response (POST /plan)
export interface DeepModePlanResponse {
  type: 'PLAN_READY' | 'AUTO_APPROVED'
  plan: {
    plan_id: string
    user_id: number
    company_id: number
    anchor_node: string
    user_query: string
    mode: string
    status: string
    hypotheses: Array<{
      id: string
      title: string
      description: string
      status: string
      category: string
      nodes: string[]
      node_count: number
    }>
    strategic_ghosts?: any[]
    metadata?: any
    created_at: string
    conversation_id?: number
  }
  generation_time: number
}

// Deep Mode Plan Execution Response (POST /plan/{plan_id}/approve)
export interface DeepModeExecutionResponse {
  status: string
  plan_id: string
  execution_time: number
  report_markdown: string          // Main report field
  context_id: string               // Strategy map context ID
  metadata: {
    nodes_assessed: number
    high_relevance_nodes: number
    validated_nodes_count: number
    excluded_hypotheses_count: number
    value_grounding_audit?: any
    strategy_map_context_id: string
  }
}

// Legacy Deep Mode Response (for backward compatibility)
export interface DeepModeResponse {
  plan_id?: string
  conversation_id?: string
  status?: string
  report?: string
  report_markdown?: string
  analysis?: string
  strategy_map?: string
  plan?: any                       // Plan object from generation response
  type?: string                    // Response type
  generation_time?: number
  execution_time?: number
  context_id?: string
  metadata?: any
}

// Comprehensive Mode type definitions

// Gate 1: DDCA Scope Definition
export interface DDCAScope {
  constants: { [key: string]: any }
  variables: { [key: string]: any }
  framework: { [key: string]: any }
}

// Gate 2: Coverage Report
export interface CoverageReport {
  coverage_distribution: {
    taxonomies: Record<string, number>
    bsc_perspectives: Record<string, number>
  }
  proposed_factors: Array<{
    factor_id: string
    factor_name: string
    taxonomy: string
    perspective: string
    relevance_score: number
    reasoning: string
    node_id?: string
    name?: string
  }>
  coverage_attestation: string
}

// Gate 3: MECE Table
// KPI metric associated with a tactic via MEASURES relationships
export interface TacticMetric {
  id: string
  name: string
  formula?: string
  unit?: string
  category?: string
  image_file?: string
  target?: {
    value: string
    unit?: string
    horizon?: string
  }
}

// KSA competency requirement via APPLIES_TO relationships
export interface TacticKSA {
  id: string
  name: string
  knowledge?: string[]
  skills?: string[]
  abilities?: string[]
  summary?: string
  source_file?: string
}

// Reference document citation via ILLUSTRATES relationships
export interface TacticRefDoc {
  id: string
  name: string
  description?: string
  source_file?: string
  excerpt?: string
}

// Process document linked via DOCUMENTS relationships
export interface TacticProcess {
  id: string
  name: string
  description?: string
  summary?: string
  source_file?: string
}

export interface MECETactic {
  title: string
  description: string
  rationale?: string
  node_ids?: string[]
  priority?: number
  relevance_score?: number
  metrics?: TacticMetric[]
  ksas?: TacticKSA[]
  ref_docs?: TacticRefDoc[]
  process_docs?: TacticProcess[]
}

export interface MECEPillar {
  name: string                    // Backend: "name" not "pillar_name"
  description: string
  priority_rank: number
  domain_keywords?: string[]
  constituent_factors?: string[]
  tactics: MECETactic[]          // Backend: "tactics" not "scenarios"
  strategic_result?: string       // LLM-generated outcome statement (Pillar.strategic_result)
}

export interface MECETable {
  pillars: MECEPillar[]
  mece_score: number | null  // Allow null after user edits (requires recalculation)
  gap_analysis?: string
  objective?: string
  context?: string
}

// Gate 3: Strategy Map (Legacy format - kept for backward compatibility)
export interface StrategyMapPerspective {
  perspective_id: string
  perspective_name: string
  objectives: Array<{
    objective_id: string
    objective_name: string
    description: string
  }>
}

// Gate 3: Strategy Map (Full CRUD schema matching backend)
export type BSCPerspective = "Financial" | "Customer" | "Internal Process" | "Learning & Growth"
export type OutcomeType = "Leading" | "Lagging"
export type RelationshipType = "LEADS_TO" | "ENABLES" | "DRIVES" | "BUILDS"
export type Strength = "HIGH" | "MEDIUM" | "LOW"
export type DecisionDomain = "external" | "internal" | "hybrid"
export type Confidence = "HIGH" | "MEDIUM" | "LOW"

// MECE-Strategy Map Tactic Reference Integration (Jan 2026)
export type TacticOrigin = "mece" | "rescue" | "augmented" | "template"

// Rich tactic reference with full context
export interface TacticReference {
  ref: string                     // "1.1", "2.3" - the tactic number
  title: string                   // Tactic title for display
  pillar_name: string             // Source pillar name (e.g., "CUSTOMER ACQUISITION")
  pillar_rank: number             // Pillar priority rank (1, 2, 3...)
  tactic_priority: number         // Tactic priority within pillar
}

// Reverse mapping: which initiatives reference a given MECE tactic
export interface TacticToInitiativeMapping {
  tactic_ref: string              // "1.1", "2.3"
  tactic_title: string            // For display
  pillar_name: string             // Source pillar
  referencing_initiatives: string[] // ["I-A1", "I-C2"] - initiative IDs
  referencing_objectives: string[]  // ["F1", "C2"] - objective IDs
}

// Bidirectional coherence index for strategic coherence queries
export interface CoherenceIndex {
  initiative_to_tactics: Record<string, string[]>  // initiative_id -> [tactic_refs]
  tactic_to_initiatives: Record<string, string[]>  // tactic_ref -> [initiative_ids]
  tactic_mappings: TacticToInitiativeMapping[]     // Rich reverse mapping for display
  orphaned_tactics: string[]                       // Tactics with no initiatives
  ungrounded_initiatives: string[]                 // Initiatives with no tactics
}

// Numeric coherence metrics for Analysis Report display
export interface CoherenceMetrics {
  tactic_coverage_pct: number       // % of MECE tactics referenced
  initiative_grounding_pct: number  // % of initiatives grounded
  overall_coherence_score: number   // Combined score
  total_mece_tactics: number
  referenced_tactics: number
  orphaned_tactics: number
  total_initiatives: number
  mece_origin_initiatives: number
  grounded_initiatives: number
}

// Pass/fail status for coherence validation
export interface CoherenceStatus {
  traceability_passed: boolean      // All MECE-origin initiatives have refs
  completeness_passed: boolean      // All MECE tactics are referenced
}

export interface StrategyObjective {
  objective_id: string              // Format: [FCPL]\d+ (e.g., F1, C2, P3, L1)
  perspective: BSCPerspective
  strategic_intent: string          // Main objective statement
  outcome_type: OutcomeType
  company_specific: boolean
  description: string
}

// Jan 2026: Strategic Objective for Analysis Report display
// Shows the "WHY" behind initiatives with initiative counts
export interface StrategicObjectiveForReport {
  objective_id: string              // "F1", "C1", "P1", "L1"
  perspective: string               // "Financial", "Customer", etc.
  strategic_intent: string          // "Grow Product Sales Revenue"
  description: string               // Detailed description
  outcome_type: 'Leading' | 'Lagging'
  initiative_count: number          // How many initiatives support this objective
}

// Company Intelligence data from CompanyIntelligenceAnalyzer
export interface CompanyIntelligenceData {
  company_name?: string
  industry_description?: string
  naics_code?: string
  revenue_tier?: string
  business_model_summary?: string
  value_proposition?: string
  key_capabilities?: string[]
  strategic_challenges?: string[]
  competitive_positioning?: string
  products_services_summary?: string
  target_customers?: string
  size_constraints?: string[]
}

// Analysis Report Data structure
export interface AnalysisReportData {
  coverageAttestation?: string
  strategicSynthesis?: {
    causal_narrative?: string
    pillar_derivation?: any[]
    methodology_summary?: {
      questions_executed?: number
      nodes_explored?: number
      nodes_approved?: number
      overall_confidence?: number
    }
  }
  strategicObjectives?: StrategicObjectiveForReport[]  // Jan 2026: BSC Strategic Objectives
  companyIntelligence?: CompanyIntelligenceData  // Feb 2026: Analysis Report redesign
  meceAnalysis?: string
  deepModePlan?: any
  deepModeReport?: any
  planGenerated?: boolean
  planExecuted?: boolean
  fullReport?: string
  strategyPlanGeneratedAt?: string
  quickResponses?: Array<{
    question: string
    answer: string
    timestamp?: string
  }>
  // Phase 5: server-computed narrative-staleness block for the
  // StaleNarrativeBanner + refresh-narratives flow.
  narrativeStaleness?: NarrativeStaleness
}

export interface ObjectiveCausalChain {
  from_objective_id: string         // Source objective (cause)
  to_objective_id: string           // Target objective (effect)
  relationship_type: RelationshipType
  strength: Strength
  rationale: string                 // Why this relationship exists
  mediation_path?: string[]         // Optional intermediate capabilities
}

export interface InitiativeMapping {
  initiative_id: string             // References MECE tactic ID
  target_objective_id: string       // Which objective this tactic supports
  decision_domain: DecisionDomain
  activation_condition: string
  confidence: Confidence
}

export interface StrategyMapMetadata {
  last_updated: string
  normalized: boolean
  removed_orphans?: {
    causal_chains?: number
    initiative_mappings?: number
    pillars?: number
    tactics?: number
  }
}

export interface StrategyMap {
  // New schema (full CRUD support)
  objectives?: StrategyObjective[]
  causal_chains?: ObjectiveCausalChain[]
  initiative_mappings?: InitiativeMapping[]
  metadata?: StrategyMapMetadata

  // Strategy Map pillars format (from backend comprehensive mode)
  pillars?: Array<{
    name: string
    description?: string
    objective_id?: string
    bsc_perspective?: BSCPerspective
    ivp_priority?: boolean
    tactics: Array<{
      tactic_id?: string
      initiative_id?: string
      title: string
      description: string
      causal_path?: any
      data_citations?: any[]
      confidence?: string | { level: string; score: number; factors?: any; assumptions?: string[] }
      // MECE-Strategy Map Tactic Reference Integration (Jan 2026)
      tactic_refs?: TacticReference[]   // Rich references to source MECE tactics
      tactic_origin?: TacticOrigin      // "mece" | "rescue" | "augmented" | "template"
      mece_pillar?: string
      source_pillar?: string
      is_augmented?: boolean
      // KPI Grounding: Metrics carried from MECE tactic enrichment
      metrics?: TacticMetric[]
      // Competency Grounding: KSAs carried from MECE tactic enrichment
      ksas?: TacticKSA[]
      // Evidence Grounding: Ref_docs carried from MECE tactic enrichment
      ref_docs?: TacticRefDoc[]
      // Process Intelligence: Process_docs carried from MECE tactic enrichment
      process_docs?: TacticProcess[]
    }>
  }>

  // Legacy schema (backward compatibility with existing renderer)
  perspectives?: StrategyMapPerspective[]
  causal_links?: Array<{
    source_objective_id: string
    target_objective_id: string
    relationship: string
  }>

  // MECE-Strategy Map Coherence Validation (Jan 2026)
  coherence_status?: CoherenceStatus
  coherence_index?: CoherenceIndex
  coherence_metrics?: CoherenceMetrics
  validation_warnings?: string[]
}

// Comprehensive Workflow with typed gates
export interface ComprehensiveWorkflow {
  workflow_id: string
  conversation_id: string
  current_gate: 1 | 2 | 3 | 'completed'
  ddca_scope?: DDCAScope
  coverage_report?: CoverageReport
  mece_table?: MECETable
  strategy_map?: StrategyMap
  graph_context?: GraphContext
}

// Business Graph Tab - Strategic Intelligence Visualization (Jan 2026)
export interface GraphContext {
  anchor_ids: string[]
  explored_ids: string[]
  zoom_center?: { x: number; y: number } | null
  taxonomy_distribution?: Record<string, number>
  bsc_distribution?: Record<string, number>
  confidence_score?: number
  causal_chain_summaries?: CausalChainSummary[]
  causal_chain_visualization?: CausalChainVisualization  // Phase F: visualization edges
  insights?: GraphInsights
  // P1: UI state persistence (Jan 2026)
  show_causal_chain?: boolean
  filter_mode?: GraphFilterMode
}

export interface CausalChainSummary {
  anchor: string
  chain_length: number
  bsc_progression: string[]
}

// Causal chain visualization data (Phase F)
export interface CausalChainEdge {
  source_id: string
  target_id: string
  relationship: string       // DEPENDS_ON, ENABLES, etc.
  source_perspective?: string // BSC perspective of source node
  target_perspective?: string // BSC perspective of target node
}

export interface CausalChainVisualization {
  objective_id: string
  objective_name: string
  edges: CausalChainEdge[]
  layers: Record<string, string[]>  // BSC perspective -> node IDs
}

// Strategic insights from graph exploration (Phase E)
export interface GraphInsights {
  blind_spots: Record<string, string[]>  // BSC perspective -> unexplored node IDs
  blind_spot_prompt: string | null       // User-facing prompt about blind spots
  cross_pillar_dependencies: CrossPillarDependency[]
  exploration_prompts: string[]
  focus_drift_warning?: string           // Warning about strategic focus drift
}

export interface CrossPillarDependency {
  pillars: number[]
  shared_nodes: string[]
  insight: string
}

// Node visual states for Business Graph
export type NodeVisualState = 'anchor' | 'explored' | 'unexplored' | 'external'

// Filter modes for Business Graph
export type GraphFilterMode = 'all' | 'related' | 'unrelated' | 'company_info'

// Extended GraphState with exploration context
export interface GraphExplorationState {
  anchorIds: string[]
  exploredIds: string[]
  filterMode: GraphFilterMode
  zoomCenter: { x: number; y: number } | null
  confidenceScore: number
  taxonomyDistribution: Record<string, number>
  bscDistribution: Record<string, number>
  insights: GraphInsights | null
}

// ============================================
// Phase 6: Advanced Interactions (Jan 2026)
// ============================================

// Implementation status for tactics
export type ImplementationStatus =
  | 'not_started'
  | 'planning'
  | 'in_progress'
  | 'completed'
  | 'blocked'
  | 'not_applicable'

// Tactic annotation for user notes
export interface TacticAnnotation {
  id: string
  tactic_id: string
  content: string
  author: string
  timestamp: string
  annotation_type: 'note' | 'concern' | 'question' | 'action_item'
}

// User state for a tactic (priority, status, annotations)
export interface TacticUserState {
  tactic_id: string
  pillar_index: number
  tactic_index: number
  priority_order: number           // User-defined priority within pillar (drag-and-drop)
  implementation_status: ImplementationStatus
  annotations: TacticAnnotation[]
  assignee?: string
  due_date?: string
  estimated_effort?: 'low' | 'medium' | 'high'
  last_updated: string
}

// Pillar-level user state
export interface PillarUserState {
  pillar_index: number
  pillar_name: string
  priority_order: number           // User-defined pillar priority (drag-and-drop)
  tactics: TacticUserState[]
  collapsed?: boolean
}

// Full MECE user state (combines all user interactions)
export interface MECEUserState {
  conversation_id: number
  pillars: PillarUserState[]
  last_saved: string
  version: number
}

// Strategy Playbook Export configuration
export interface PlaybookExportConfig {
  title: string
  include_annotations: boolean
  include_evidence: boolean
  include_status: boolean
  include_timeline: boolean
  filter_status?: ImplementationStatus[]   // Only include tactics with these statuses
  filter_pillars?: number[]                // Only include specific pillars by index
  format: 'markdown' | 'html' | 'pdf'
  custom_header?: string
  custom_footer?: string
}

// Playbook export result
export interface PlaybookExportResult {
  filename: string
  content: string
  format: 'markdown' | 'html' | 'pdf'
  generated_at: string
  stats: {
    total_pillars: number
    total_tactics: number
    tactics_by_status: Record<ImplementationStatus, number>
    annotations_count: number
  }
}

// Status display metadata
export const IMPLEMENTATION_STATUS_META: Record<ImplementationStatus, {
  label: string
  color: string
  icon: string
  description: string
}> = {
  not_started: {
    label: 'Not Started',
    color: '#6b7280',
    icon: '○',
    description: 'Work has not begun on this tactic'
  },
  planning: {
    label: 'Planning',
    color: '#f59e0b',
    icon: '◐',
    description: 'Currently being planned or scoped'
  },
  in_progress: {
    label: 'In Progress',
    color: '#3b82f6',
    icon: '◑',
    description: 'Active work is underway'
  },
  completed: {
    label: 'Completed',
    color: '#10b981',
    icon: '●',
    description: 'Successfully implemented'
  },
  blocked: {
    label: 'Blocked',
    color: '#ef4444',
    icon: '⊘',
    description: 'Progress is blocked by dependencies'
  },
  not_applicable: {
    label: 'N/A',
    color: '#9ca3af',
    icon: '—',
    description: 'Not applicable to this context'
  }
}

// Annotation type metadata
export const ANNOTATION_TYPE_META: Record<TacticAnnotation['annotation_type'], {
  label: string
  color: string
  icon: string
}> = {
  note: { label: 'Note', color: '#6b7280', icon: '📝' },
  concern: { label: 'Concern', color: '#f59e0b', icon: '⚠️' },
  question: { label: 'Question', color: '#3b82f6', icon: '❓' },
  action_item: { label: 'Action Item', color: '#10b981', icon: '✓' }
}

// ============================================
// Phase 6.5: What-If Scenario Exploration (Jan 2026)
// ============================================

// Tactic inclusion state for scenarios
export type TacticInclusionState = 'included' | 'excluded' | 'maybe'

// Individual tactic selection in a scenario
export interface ScenarioTacticSelection {
  pillar_index: number
  tactic_index: number
  inclusion: TacticInclusionState
  impact_notes?: string                    // User notes about why included/excluded
}

// A what-if scenario configuration
export interface WhatIfScenario {
  id: string
  name: string
  description?: string
  created_at: string
  updated_at: string
  tactic_selections: ScenarioTacticSelection[]
  is_baseline: boolean                     // True for the "current state" baseline
}

// Impact metrics for a scenario
export interface ScenarioImpactMetrics {
  total_tactics: number
  included_tactics: number
  excluded_tactics: number
  maybe_tactics: number
  coverage_by_pillar: Record<string, {
    total: number
    included: number
    percentage: number
  }>
  bsc_coverage: Record<string, number>     // BSC perspective coverage percentages
  estimated_effort: {
    low: number
    medium: number
    high: number
  }
  risk_score: number                       // 0-100, higher = more risk from exclusions
  strategic_alignment_score: number        // 0-100, based on included high-priority tactics
}

// Comparison between two scenarios
export interface ScenarioComparison {
  scenario_a: WhatIfScenario
  scenario_b: WhatIfScenario
  metrics_a: ScenarioImpactMetrics
  metrics_b: ScenarioImpactMetrics
  differences: {
    newly_included: ScenarioTacticSelection[]
    newly_excluded: ScenarioTacticSelection[]
    coverage_delta: number                 // Percentage point change
    effort_delta: number                   // Estimated effort change
    risk_delta: number                     // Risk score change
  }
}

// Full scenario exploration state
export interface ScenarioExplorationState {
  scenarios: WhatIfScenario[]
  active_scenario_id: string | null
  comparison_mode: boolean
  comparison_scenario_ids: [string, string] | null
  show_impact_panel: boolean
}

// Scenario display metadata
export const TACTIC_INCLUSION_META: Record<TacticInclusionState, {
  label: string
  color: string
  icon: string
  description: string
}> = {
  included: {
    label: 'Included',
    color: '#10b981',
    icon: '✓',
    description: 'This tactic will be implemented'
  },
  excluded: {
    label: 'Excluded',
    color: '#ef4444',
    icon: '✗',
    description: 'This tactic will NOT be implemented'
  },
  maybe: {
    label: 'Maybe',
    color: '#f59e0b',
    icon: '?',
    description: 'Decision pending - needs further analysis'
  }
}

// ============================================================================
// Oracle Insights Types (re-export from oracle.ts)
// ============================================================================

export * from './oracle'
